/**
 * useDashboard hooks — Issue #189
 *
 * Typed hooks for Dashboard read model and async export task API calls,
 * using @jinzhuan/api-client.
 *
 * Red lines:
 *   - MUST use @jinzhuan/api-client; no hand-written fetch / axios.
 *   - No fake frontend state; metrics with status='unavailable' MUST render as
 *     "待接入"; never substitute a fake zero or random number.
 *   - Decimal amounts are transmitted as strings; do not call parseFloat /
 *     Number / toFixed on them. Use decimal.js for any local arithmetic.
 */

import { useCallback, useState } from 'react';
import {
  createApiClient,
  type ApiPaths,
  type CancelExportTaskRequest,
  type CreateExportTaskRequest,
  type DashboardMetric,
  type DashboardOverviewResponse,
  type ExportDownloadUrlResponse,
  type ExportTaskDto,
  type RequestExportDownloadUrlRequest,
  type RevenueSummaryResponse,
} from '@/lib/adminApiClient';

type ListExportTaskQuery =
  NonNullable<ApiPaths['/api/v1/dashboard/exports']['get']['parameters']['query']>;
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export interface DashboardApiError {
  readonly message: string;
  readonly code?: string;
}

function toApiError(err: unknown, httpStatus?: number): DashboardApiError {
  if (httpStatus === 401) return { message: '未登录或会话已过期', code: 'UNAUTHENTICATED' };
  if (httpStatus === 403) return { message: '当前角色没有看板/导出权限', code: 'FORBIDDEN' };
  if (httpStatus === 409) return { message: '导出任务已被其他操作修改，请刷新后重试', code: 'CONFLICT' };
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

export type DashboardOverview = DashboardOverviewResponse;
export type RevenueSummary = RevenueSummaryResponse;
export type DashboardMetricView = DashboardMetric;

// ─────────────────────────────────────────────────────────────────────────────
// Overview hook
// ─────────────────────────────────────────────────────────────────────────────

export function useDashboardOverview() {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DashboardApiError | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/dashboard/overview',
        {},
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) setOverview(data);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { overview, loading, error, fetchOverview };
}

// ─────────────────────────────────────────────────────────────────────────────
// Revenue summary hook
// ─────────────────────────────────────────────────────────────────────────────

export function useRevenueSummary() {
  const [summary, setSummary] = useState<RevenueSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DashboardApiError | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/dashboard/revenue-summary',
        {},
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) setSummary(data);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { summary, loading, error, fetchSummary };
}

// ─────────────────────────────────────────────────────────────────────────────
// Export tasks hook
// ─────────────────────────────────────────────────────────────────────────────

export interface ListExportTasksParams {
  readonly exportType?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly offset?: number;
}

export function useExportTasks() {
  const [tasks, setTasks] = useState<ExportTaskDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DashboardApiError | null>(null);

  const fetchTasks = useCallback(async (params?: ListExportTasksParams) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/dashboard/exports',
        {
          params: {
            query: {
              ...(params?.exportType
                ? { exportType: params.exportType as ListExportTaskQuery['exportType'] }
                : {}),
              ...(params?.status
                ? { status: params.status as ListExportTaskQuery['status'] }
                : {}),
              ...(params?.limit !== undefined ? { limit: params.limit } : {}),
              ...(params?.offset !== undefined ? { offset: params.offset } : {}),
            },
          },
        },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) setTasks([...data]);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const createTask = useCallback(
    async (body: CreateExportTaskRequest): Promise<ExportTaskDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/dashboard/exports',
          { body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancelTask = useCallback(
    async (id: string, body: CancelExportTaskRequest): Promise<ExportTaskDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/dashboard/exports/{id}/cancel',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const requestDownloadUrl = useCallback(
    async (
      id: string,
      body: RequestExportDownloadUrlRequest = {},
    ): Promise<ExportDownloadUrlResponse | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/dashboard/exports/{id}/download-url',
          { params: { path: { id } }, body },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      }
    },
    [],
  );

  return { tasks, loading, error, fetchTasks, createTask, cancelTask, requestDownloadUrl };
}
